/**
 * AMEN — the fixed-slice break player.
 *
 * Chirp provenance: Chirp/shrike.html (playSlice, the BREAKS manifest,
 * loadBreak/acceptFile/acceptBuffer). Simplified to a FIXED chop; transient
 * detection is not transferred.
 *
 * The bundled Amen is a verified CC0 re-recording inherited from Chirp's break
 * crate. The manifest carries its exact source, creator, licence, lineage and
 * file hash. A local file picker remains available as a session-only override.
 */

import { createBreakPlayer, type BreakPlayer } from '../audio/breaks/slicer';
import {
  BREAK_MANIFEST, reconstructPattern, rotatePattern, stutterPattern,
  isPlayable, validateManifest, type BreakEntry,
} from '../audio/breaks/manifest';
import type { Task } from '../audio/transport';
import { BreaksAdapter } from '../mapping/adapters';
import { getExperiments } from '../mapping/config';
import type { BreaksIntent, ControlState, FeatureFrame } from '../mapping/types';
import { button, chips, el, panel, slider, toggle } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createBreaksScene(): Scene {
  let ctxRef: SceneContext | null = null;
  let player: BreakPlayer | null = null;
  let buffer: AudioBuffer | null = null;
  let task: Task | null = null;
  const entry: BreakEntry = { ...BREAK_MANIFEST[0] };
  let slicesPerBar = entry.slices;
  let sliceCount = entry.slices * entry.bars;
  let pattern = reconstructPattern(sliceCount);
  let cursor = 0;
  /** Which bar the height axis selects slices from. Chips, 0-based. */
  let bar = 0;
  let bpm = entry.bpm ?? 136;
  const adapter = new BreaksAdapter();
  let last: BreaksIntent | null = null;
  let sourceName = '';

  let missingBox: HTMLElement;
  let loadedBox: HTMLElement;
  let patternRow: HTMLElement;

  function drawPattern(): void {
    // `on` marks the playing position; `sel` marks every cell holding the slice
    // the hand is pointing at, so you can see where "your" slice sits in the
    // running pattern before you fire it.
    const selected = last?.selectedSlice ?? -1;
    patternRow.replaceChildren(...pattern.map((s, i) =>
      el('i', {
        class: `step${i === cursor % pattern.length ? ' on' : ''}${s === selected ? ' sel' : ''}`,
      }, [String(s + 1)])));
  }

  function setPattern(p: number[]): void { pattern = p; drawPattern(); }

  function start(): void {
    if (!ctxRef || task || !player) return;
    task = ctxRef.transport.addTask({
      bpm, division: slicesPerBar / 4,
      cb: (t, i) => {
        cursor = i % pattern.length;
        player?.play(pattern[cursor], t, 0.95, last?.rate ?? 1);
        drawPattern();
      },
    });
  }
  function stop(): void { task?.cancel(); task = null; }

  async function accept(file: File): Promise<void> {
    if (!ctxRef) return;
    const ab = await file.arrayBuffer();
    await acceptBuffer(ab, file.name);
    ctxRef?.setStatus(`Loaded ${file.name} from your device. Nothing was uploaded and nothing is stored.`);
  }

  async function acceptBuffer(ab: ArrayBuffer, name: string): Promise<void> {
    if (!ctxRef) return;
    const decoded = await ctxRef.bus.ctx.decodeAudioData(ab);
    // Re-check after the await: a fast scene switch unmounts while the decode
    // is in flight, and without this the player below would be built anyway —
    // permanently connected to the master bus, with nothing left to dispose it.
    if (!ctxRef) return;
    buffer = decoded;
    player?.dispose();
    player = createBreakPlayer(ctxRef.bus.ctx, ctxRef.bus.input, buffer, sliceCount);
    sourceName = name;
    setPattern(reconstructPattern(sliceCount));
    missingBox.hidden = true;
    loadedBox.hidden = false;
    loadedBox.querySelector('[data-name]')!.textContent =
      `${name} — ${buffer.duration.toFixed(2)}s, ${sliceCount} slices`;
  }

  async function loadBundled(): Promise<void> {
    if (!ctxRef || !isPlayable(entry) || !entry.file) return;
    try {
      const url = new URL(entry.file, document.baseURI);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const ab = await response.arrayBuffer();
      if (!ctxRef) return; // unmounted while fetching
      await acceptBuffer(ab, entry.label);
      ctxRef?.setStatus(`${entry.label} loaded — ${entry.bpm} BPM, ${entry.bars} bars, CC0.`);
    } catch (error) {
      if (!ctxRef) return;
      missingBox.hidden = false;
      missingBox.querySelector('[data-error]')!.textContent =
        `Bundled sample could not be decoded (${error instanceof Error ? error.message : 'unknown error'}). Choose a local audio file to continue.`;
      ctxRef.setStatus('Amen sample failed to load. Use the file picker as a fallback.');
    }
  }

  return {
    id: 'breaks',
    label: 'Amen',
    blurb: 'A four-bar CC0 Amen remake, chopped into eighth-note windows. Raise or lower the pitch hand to aim at a slice of the current bar, pinch to fire it.',
    padMode: 'xy',
    // The height axis addresses one bar's slices — wide zones, like pads.
    selectionSteps: entry.slices,

    mount(host, ctx) {
      ctxRef = ctx;
      const issues = validateManifest(BREAK_MANIFEST);

      const input = el('input', {
        type: 'file', id: 'break-file', accept: 'audio/*', class: 'file',
      }) as HTMLInputElement;
      input.addEventListener('change', () => {
        const f = input.files?.[0];
        if (f) void accept(f);
      });

      missingBox = el('div', { class: 'missing', role: 'status' }, [
        el('h3', {}, ['Loading bundled Amen']),
        el('p', { 'data-error': '' }, [
          'The CC0 Amen remake is loading. If it cannot be decoded on this device, choose another local break as a session-only fallback.',
        ]),
        el('label', { class: 'filelabel', for: 'break-file' }, ['Choose fallback audio']),
        input,
      ]);

      loadedBox = el('div', { class: 'loaded', hidden: 'true' }, [
        el('b', { class: 'read wide', 'data-name': '' }, ['—']),
      ]);

      patternRow = el('div', { class: 'stepgrid pattern', 'aria-label': 'Slice pattern' });
      drawPattern();

      host.append(panel('Amen slicer', 'Four bars chopped into equal eighth-note windows and retriggered on the grid — the REX idea, without transient detection.', [
        missingBox,
        loadedBox,
        patternRow,
        chips<string>({
          label: 'Bar', value: '0',
          options: Array.from({ length: entry.bars }, (_, i) => ({
            id: String(i), label: `Bar ${i + 1}`, title: `Aim the height axis at bar ${i + 1}'s slices`,
          })),
          onChange: (id) => { bar = Number(id); },
        }),
        slider({
          label: 'Slices / bar', min: 4, max: 16, step: 4, value: slicesPerBar, format: (v) => `${v}`,
          onInput: (v) => {
            slicesPerBar = v;
            sliceCount = v * entry.bars;
            setPattern(reconstructPattern(sliceCount));
            if (buffer && ctxRef) {
              player?.dispose();
              player = createBreakPlayer(ctxRef.bus.ctx, ctxRef.bus.input, buffer, sliceCount);
            }
            task?.setDivision(slicesPerBar / 4);
            // The height axis must re-quantize to the new slice count, or the
            // hand would keep addressing zones that no longer exist.
            ctxRef?.setSelectionSteps(v);
          },
        }),
        slider({ label: 'Tempo', min: 80, max: 200, step: 1, value: bpm, format: (v) => `${v} bpm`, onInput: (v) => { bpm = v; task?.setBpm(v); } }),
        el('div', { class: 'row' }, [
          button('Reconstruct', () => setPattern(reconstructPattern(sliceCount)), 'ghost'),
          button('Rotate', () => setPattern(rotatePattern(pattern, 1)), 'ghost'),
          button('Stutter', () => setPattern(stutterPattern(pattern)), 'ghost'),
          toggle('Run', false, (on) => { on ? start() : stop(); }, 'Play the pattern'),
        ]),
        issues.length
          ? el('p', { class: 'note warn' }, [`manifest: ${issues.map((i) => `${i.id}.${i.field} — ${i.message}`).join('; ')}`])
          : el('p', { class: 'note' }, ['manifest: valid']),
      ]));
      ctx.setStatus('Amen — loading the bundled CC0 four-bar remake.');
      void loadBundled();
    },

    update(frame: FeatureFrame, control: ControlState) {
      const prevSelected = last?.selectedSlice;
      const intent = adapter.update(frame, control, slicesPerBar, bar);
      last = intent;
      if (intent.selectedSlice !== prevSelected) drawPattern();
      player?.setLevel(intent.level);
      // Gate 4: quantized books the slice on the pattern grid, sampling rate
      // at fire-decision time; immediate fires it on the spot.
      if (intent.triggerSlice >= 0 && player && ctxRef) {
        if (getExperiments().trigger === 'quantized') {
          const slice = intent.triggerSlice;
          ctxRef.transport.at(
            ctxRef.transport.nextGrid(bpm, slicesPerBar / 4),
            (t) => player?.play(slice, t, 0.95, last?.rate ?? 1),
          );
        } else {
          player.play(intent.triggerSlice, ctxRef.bus.ctx.currentTime + 0.02, 0.95, intent.rate);
        }
      }
    },

    readouts() {
      return [
        ['slice', last ? `${(last.selectedSlice % slicesPerBar) + 1} of bar ${bar + 1}` : '—'],
        ['source', sourceName || 'none'],
        ['slices', `${sliceCount} (${slicesPerBar}/bar)`],
        ['rate', last ? `${last.rate.toFixed(2)}x` : '—'],
        ['level', last ? last.level.toFixed(2) : '—'],
      ];
    },

    selection() {
      if (!last) return null;
      const inBar = last.selectedSlice % slicesPerBar;
      return { count: slicesPerBar, active: inBar, label: `slice ${inBar + 1} · bar ${bar + 1}` };
    },

    unmount() { stop(); player?.dispose(); player = null; adapter.reset(); ctxRef = null; },
  };
}
